import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { api, ErrorApi } from '../api';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Seccion, CampoValidado } from '../ui/Formulario';
import { etiquetaRol } from '../lib/roles';
import { Cargando } from '../ui/EstadoDePantalla';

const ROLES = ['ENCARGADO', 'ADMIN', 'DUENO'];
// ISO: 1 = lunes … 7 = domingo, igual que EXTRACT(ISODOW) en el backend.
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
// Sugerencias, no una lista cerrada: el backend acepta cualquier nombre de
// la base IANA, que es lo que necesita una empresa con almacenes fuera.
const ZONAS = [
  'America/Mexico_City', 'America/Monterrey', 'America/Tijuana',
  'America/Cancun', 'America/Chihuahua', 'America/Bogota',
  'America/Lima', 'America/Santiago', 'America/New_York',
  'America/Los_Angeles', 'Europe/Madrid', 'UTC',
];

// Donde se enciende la capa de control. Nace apagada a propósito: los
// umbrales dependen del almacén, y un valor inventado por el sistema
// dejaría a media bodega esperando aprobaciones el lunes por la mañana.
// El horario se define entero o no se define: con una sola hora no hay
// turno que comparar, y la vigilancia quedaria encendida sin criterio.
function faltaHora(f) {
  const ini = (f.hora_inicio_laboral || '').trim();
  const fin = (f.hora_fin_laboral || '').trim();
  if (Boolean(ini) === Boolean(fin)) return null;
  return ini ? 'Falta la hora de fin.' : 'Falta la hora de inicio.';
}

export default function ConfiguracionControl() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vigente = true;
    api
      .get('/configuracion/control')
      .then((d) => {
        if (!vigente) return;
        setCfg(d);
        setF({
          control_activo: d.control_activo,
          umbral_valor: d.umbral_valor ?? '',
          umbral_cantidad: d.umbral_cantidad ?? '',
          rol_minimo_aprobador: d.rol_minimo_aprobador,
          dias_vigencia_solicitud: d.dias_vigencia_solicitud,
          anomalias_activas: d.anomalias_activas,
          hora_inicio_laboral: (d.hora_inicio_laboral || '').slice(0, 5),
          hora_fin_laboral: (d.hora_fin_laboral || '').slice(0, 5),
          dias_laborales: d.dias_laborales || [1, 2, 3, 4, 5, 6],
          rafaga_n: d.rafaga_n,
          rafaga_minutos: d.rafaga_minutos,
          zona_horaria: d.zona_horaria,
        });
      })
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, []);

  if (error && !f) return <Aviso texto={error} />;
  if (!f) return <Cargando filas={4} />;

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function guardar(e) {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setGuardando(true);

    // Un campo vacío significa "sin umbral", y eso hay que decirlo
    // explícitamente: mandar null en una actualización parcial no se
    // distingue de "no lo toques".
    const limpiar = [];
    const cuerpo = {
      control_activo: f.control_activo,
      rol_minimo_aprobador: f.rol_minimo_aprobador,
      dias_vigencia_solicitud: Number(f.dias_vigencia_solicitud),
      anomalias_activas: f.anomalias_activas,
      dias_laborales: f.dias_laborales,
      rafaga_n: Number(f.rafaga_n),
      rafaga_minutos: Number(f.rafaga_minutos),
      zona_horaria: f.zona_horaria,
    };
    for (const campo of ['umbral_valor', 'umbral_cantidad',
                         'hora_inicio_laboral', 'hora_fin_laboral']) {
      if (f[campo] === '' || f[campo] === null) limpiar.push(campo);
      else cuerpo[campo] = campo.startsWith('hora') ? f[campo] : Number(f[campo]);
    }
    cuerpo.limpiar = limpiar;

    try {
      setCfg(await api.put('/configuracion/control', cuerpo));
      setMsg('Configuración guardada.');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Cuándo pedir visto bueno</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Cuánto puede salir del almacén sin que lo apruebe una segunda persona, y qué patrones vigilar.
        </p>
      </div>

      <NotaExplicativa titulo="Cómo funciona el principio de cuatro ojos">
        Cuando una salida, una merma o un ajuste a la baja alcanza el umbral, el movimiento
        <strong> no se aplica</strong>: queda esperando en «Salidas por aprobar» y la mercancía sigue donde está.
        Quien lo pidió no puede autorizarlo. El umbral de <strong>valor</strong> es el natural, pero un
        producto que nunca tuvo una entrada costeada vale cero y se le escaparía — fija
        también el de <strong>cantidad</strong>.
      </NotaExplicativa>

      <form onSubmit={guardar} className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={f.control_activo}
            onChange={(e) => setF((s) => ({ ...s, control_activo: e.target.checked }))}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900">Exigir autorización doble</span>
            <span className="block text-xs text-gray-500">
              Con esto apagado, todo se aplica al instante (es como funcionaba antes).
            </span>
          </span>
        </label>

        <Seccion titulo="A partir de cuándo se pide permiso"
                 descripcion="Cualquiera de los dos umbrales dispara la autorización. Un producto que nunca tuvo entrada costeada vale cero, y solo el de cantidad lo atrapa.">
          <CampoValidado etiqueta="Umbral de valor (MXN)" valor={f.umbral_valor}
                         ayuda="Vacío = no aplica."
                         validar={(v) => (!String(v).trim() || Number(v) >= 0
                           ? null : 'No puede ser negativo.')}>
            {(props) => <input {...props} type="number" step="any" min="0" value={f.umbral_valor}
                               onChange={set('umbral_valor')} placeholder="Sin umbral" />}
          </CampoValidado>
          <CampoValidado etiqueta="Umbral de cantidad" valor={f.umbral_cantidad}
                         ayuda="Vacío = no aplica."
                         validar={(v) => (!String(v).trim() || Number(v) >= 0
                           ? null : 'No puede ser negativo.')}>
            {(props) => <input {...props} type="number" step="any" min="0" value={f.umbral_cantidad}
                               onChange={set('umbral_cantidad')} placeholder="Sin umbral" />}
          </CampoValidado>
        </Seccion>

        <Seccion titulo="Quién autoriza y hasta cuándo">
          <CampoValidado etiqueta="Quién puede autorizar" valor={f.rol_minimo_aprobador}
                         ayuda="Ese rango o superior, y siempre alguien distinto de quien lo pidió.">
            {(props) => (
              <select {...props} value={f.rol_minimo_aprobador} onChange={set('rol_minimo_aprobador')}>
                {ROLES.map((r) => <option key={r} value={r}>{etiquetaRol(r)}</option>)}
              </select>
            )}
          </CampoValidado>
          <CampoValidado etiqueta="Días de vigencia" valor={f.dias_vigencia_solicitud}
                         ayuda="Pasado ese plazo la solicitud caduca: aprobar algo pedido hace semanas es peligroso."
                         validar={(v) => (Number(v) >= 1 && Number(v) <= 365
                           ? null : 'Entre 1 y 365 días.')}>
            {(props) => <input {...props} type="number" min="1" max="365"
                               value={f.dias_vigencia_solicitud}
                               onChange={set('dias_vigencia_solicitud')} />}
          </CampoValidado>
        </Seccion>

        <hr className="border-gray-200" />

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={f.anomalias_activas}
            onChange={(e) => setF((s) => ({ ...s, anomalias_activas: e.target.checked }))}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900">Vigilar patrones raros</span>
            <span className="block text-xs text-gray-500">
              Detecta lo que ningún umbral por movimiento ve: operar fuera del turno, ráfagas y salidas
              pequeñas y repetidas que sumadas superan el umbral de arriba.
            </span>
            {/* MIRA HACIA ADELANTE, Y HAY QUE DECIRLO (deuda V-4). Al
                encenderla no se revisa el histórico. Es lo correcto —el
                primer día no debe llegar una avalancha de alertas de hace
                meses— pero quien la activa esperando enterarse de lo que
                pasó el mes pasado se queda esperando, y el silencio se
                lee como «no hubo nada raro». */}
            <span className="block text-xs text-amber-700 mt-1">
              Empieza a vigilar <strong>desde que la enciendes</strong>: no revisa
              lo que ya pasó. Para mirar hacia atrás están Auditoría y el
              kárdex, que lo tienen todo desde el primer día.
            </span>
          </span>
        </label>

        <Seccion titulo="Cuál es el turno" columnas={1}>
          <CampoValidado
            etiqueta="Zona horaria del almacén" valor={f.zona_horaria}
            ayuda={`El turno se juzga con ESTE reloj, no con el del servidor. ${
              cfg?.hora_local ? `Ahí son las ${cfg.hora_local}.` : ''
            }`}
          >
            {(props) => (
              <>
                <input {...props} value={f.zona_horaria} onChange={set('zona_horaria')}
                       placeholder="America/Mexico_City" list="zonas-comunes" />
                <datalist id="zonas-comunes">
                  {ZONAS.map((z) => <option key={z} value={z} />)}
                </datalist>
              </>
            )}
          </CampoValidado>
        </Seccion>

        <Seccion titulo="Horario laboral"
                 descripcion="Se define entero o no se define: con una sola hora no hay turno que comparar.">
          <CampoValidado etiqueta="El turno empieza" valor={f.hora_inicio_laboral}
                         ayuda="Vacío = no se vigila el horario."
                         validar={() => faltaHora(f)}>
            {(props) => <input {...props} type="time" value={f.hora_inicio_laboral}
                               onChange={set('hora_inicio_laboral')} />}
          </CampoValidado>
          <CampoValidado etiqueta="y termina" valor={f.hora_fin_laboral}
                         validar={() => faltaHora(f)}>
            {(props) => <input {...props} type="time" value={f.hora_fin_laboral}
                               onChange={set('hora_fin_laboral')} />}
          </CampoValidado>
        </Seccion>

        {/* Son siete botones que responden a UNA pregunta: `fieldset` con su
            `legend`, no un `label` que apuntaría a un control inexistente. */}
        <fieldset className="block">
          <legend className="block text-sm font-semibold text-gray-700 mb-1">Días laborales</legend>
          <div className="flex flex-wrap gap-1.5">
            {DIAS.map((d, i) => {
              const numero = i + 1;
              const activo = f.dias_laborales.includes(numero);
              return (
                <button
                  type="button"
                  key={d}
                  onClick={() => setF((s) => ({
                    ...s,
                    dias_laborales: activo
                      ? s.dias_laborales.filter((x) => x !== numero)
                      : [...s.dias_laborales, numero].sort(),
                  }))}
                  className={`px-2.5 py-1.5 rounded-lg text-sm font-semibold border ${
                    activo ? 'bg-[var(--color-marca)] text-white border-[var(--color-marca)]' : 'bg-white text-gray-600 border-gray-300'
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <span className="block text-xs text-gray-500 mt-1">
            Un movimiento en un día no marcado levanta alerta.
          </span>
        </fieldset>

        <Seccion titulo="Qué cuenta como ráfaga"
                 descripcion="Salidas pequeñas y seguidas que ningún umbral por movimiento ve, pero que sumadas sí importan.">
          <CampoValidado etiqueta="Cuántos movimientos" valor={f.rafaga_n}
                         validar={(v) => (Number(v) >= 2 ? null : 'Al menos dos: uno solo no es una ráfaga.')}>
            {(props) => <input {...props} type="number" min="2" value={f.rafaga_n}
                               onChange={set('rafaga_n')} />}
          </CampoValidado>
          <CampoValidado etiqueta="…en cuántos minutos" valor={f.rafaga_minutos}
                         validar={(v) => (Number(v) >= 1 && Number(v) <= 1440
                           ? null : 'Entre 1 minuto y 24 horas.')}>
            {(props) => <input {...props} type="number" min="1" max="1440" value={f.rafaga_minutos}
                               onChange={set('rafaga_minutos')} />}
          </CampoValidado>
        </Seccion>

        {error && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}
        {msg && <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2.5">{msg}</div>}

        <button type="submit" disabled={guardando}
                className="bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-lg text-sm">
          {guardando ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </form>

      {cfg && (
        <p className="text-xs text-gray-500">
          {cfg.control_activo
            ? `Activo. Autoriza ${etiquetaRol(cfg.rol_minimo_aprobador)} o superior.`
            : 'Apagado: ningún movimiento requiere autorización.'}
          {cfg.actualizado_por ? ` Último cambio: ${cfg.actualizado_por}.` : ''}
        </p>
      )}
    </div>
  );
}


function Aviso({ texto }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
      <ShieldAlert className="h-5 w-5 shrink-0" />
      <span>{texto}</span>
    </div>
  );
}
